/**
 * Enhanced Profile Page Component
 * 
 * Colorful profile page with photo upload and enhanced visual design.
 * 
 * Features:
 * - Gradient header section
 * - Profile photo upload
 * - Colorful cards with gradients
 * - Enhanced visual hierarchy
 * - Same functionality as original Profile.js
 * 
 * Created: 2025-01-17 (Phase 4.8: Enhanced Profile)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileAPI } from '../services/api';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import Sidebar from '../components/dashboard/Sidebar';
import ProfileForm from '../components/profile/ProfileForm';
import ProfileDisplay from '../components/profile/ProfileDisplay';
import ProfileCompletionBanner from '../components/profile/ProfileCompletionBanner';
import ProfilePhotoUpload from '../components/profile/ProfilePhotoUpload';

const Profile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [photoUrl, setPhotoUrl] = useState(null);

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await profileAPI.getProfile();
      setProfile(response.data);
      setPhotoUrl(response.data.profile_photo_url);
      
      // Auto-enter edit mode if profile is incomplete
      if (!response.data.profile_completed) {
        setEditMode(true);
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setError(err.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (profileData) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Include photo if changed
      const dataToSave = {
        ...profileData,
        profile_photo_url: photoUrl
      };
      
      const response = await profileAPI.updateProfile(dataToSave);
      setProfile(response.data);
      setEditMode(false);
      setSuccess('Profile updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to update profile:', err);
      
      // Handle validation errors
      if (err.response?.status === 422) {
        const detail = err.response?.data?.detail;
        if (typeof detail === 'string') {
          setError(detail);
        } else if (Array.isArray(detail)) {
          setError(detail.map(e => e.msg).join(', '));
        } else {
          setError('Validation error. Please check your inputs.');
        }
      } else {
        setError(err.response?.data?.detail || 'Failed to update profile');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setPhotoUrl(profile?.profile_photo_url || null);
    setError('');
    setSuccess('');
  };

  const handleEdit = () => {
    setEditMode(true);
    setError('');
    setSuccess('');
  };

  const handleCompleteProfile = () => {
    setEditMode(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePhotoChange = (newPhotoUrl) => {
    setPhotoUrl(newPhotoUrl);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-teal-50">
        <DashboardHeader />
        <div className="flex">
          <Sidebar currentPath="/profile" />
          <main className="flex-1 lg:ml-64 p-6">
            <div className="max-w-5xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading your profile...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-teal-50">
      {/* Dashboard Header */}
      <DashboardHeader />

      {/* Layout: Sidebar + Main Content */}
      <div className="flex">
        {/* Sidebar Navigation */}
        <Sidebar currentPath="/profile" />

        {/* Main Content Area */}
        <main className="flex-1 lg:ml-64 p-6">
          <div className="max-w-5xl mx-auto">
            
            {/* Profile Completion Banner */}
            {profile && !profile.profile_completed && (
              <ProfileCompletionBanner 
                show={true}
                onComplete={handleCompleteProfile}
              />
            )}

            {/* Success Message */}
            {success && (
              <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4 shadow-md">
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm font-semibold text-green-800">{success}</p>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 rounded-xl p-4 shadow-md">
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm font-semibold text-red-800">{error}</p>
                </div>
              </div>
            )}

            {/* Profile Header Card */}
            <div className="bg-gradient-to-r from-blue-600 via-blue-500 to-teal-500 rounded-2xl shadow-2xl p-8 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-white/20 flex items-center justify-center ring-4 ring-white/50 shadow-xl">
                    {photoUrl ? (
                      <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-white">
                      {profile?.first_name} {profile?.last_name}
                    </h1>
                    <p className="text-blue-100 mt-1">{profile?.email}</p>
                  </div>
                </div>
                
                <span className={`px-4 py-2 rounded-full text-sm font-semibold shadow-lg ${
                  profile?.tier === 'premium'
                    ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white'
                    : 'bg-white text-blue-600'
                }`}>
                  {profile?.tier ? profile.tier.charAt(0).toUpperCase() + profile.tier.slice(1) : 'Free'} Tier
                </span>
              </div>
            </div>

            {/* Main Profile Card */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-8 py-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {editMode ? 'Edit Profile' : 'Profile Information'}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {editMode ? 'Update your personal information and settings' : 'Manage your account details'}
                    </p>
                  </div>
                  
                  {!editMode && (
                    <button
                      onClick={handleEdit}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-teal-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-teal-700 transition shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      Edit Profile
                    </button>
                  )}
                </div>
              </div>

              {/* Card Content */}
              <div className="p-8">
                {editMode && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Photo</h3>
                    <ProfilePhotoUpload 
                      currentPhotoUrl={photoUrl}
                      onPhotoChange={handlePhotoChange}
                    />
                    <hr className="my-8 border-gray-200" />
                  </div>
                )}

                {editMode ? (
                  <ProfileForm 
                    profile={profile}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    loading={saving}
                  />
                ) : (
                  <ProfileDisplay 
                    profile={profile}
                    onEdit={handleEdit}
                  />
                )}
              </div>
            </div>

            {/* Help Card */}
            <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 shadow-md">
              <div className="flex items-start">
                <svg className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-2">About Profile Security</p>
                  <p className="leading-relaxed">
                    Your name is locked at signup for security purposes. Your date of birth is locked after first entry to ensure tax strategy accuracy. 
                    If you need to update these locked fields, please contact our support team.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Profile;
